import React from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import Typography from '@material-ui/core/Typography';
import {Theme} from '@material-ui/core/styles';
import {
  AdRequestProffer,
  BillingPlan,
  DigiknowLocation,
  DigiknowOrgType} from '../../ext/lib/types';
import {Redirect} from 'react-router-dom';
import AdDetailsForm, {AdRequestFormFields} from './AdDetailsForm';
import AdUploadForm from './AdUploadForm';
import Review from './Review';
import UserContext from '../common/UserContext';
import {makeStyles} from '@material-ui/core/styles';
import {v4 as uuid} from 'uuid';
import {splitName, userEmail} from '../common/UserContext';
import * as R from 'ramda';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    position: 'relative'
  },
  icon: {
    marginRight: theme.spacing(2)
  },
  paper: {
    width: 800,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3)
    }
  },
  stepper: {
    padding: theme.spacing(3, 0, 5)
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1)
  }
}));

const steps = ['Ad details', 'Upload ad file', 'Review your ad'];

const CreateAd: React.FC = () => {
  // adKey is unchanging for the life of this component
  // so that multiple submits will have the same adKey
  const adKey = uuid();
  const {user, apiToken} = React.useContext(UserContext);
  const [step, setStep] = React.useState(0);
  const [adName, setAdName] = React.useState('');
  const [comments, setComments] = React.useState('');
  const [organization, setOrganization] = React.useState('');
  const [orgType, setOrgType] = React.useState('');
  const [phoneNum, setPhoneNum] = React.useState('');
  const [rechargeNum, setRechargeNum] = React.useState(undefined as string | undefined);
  const [billingPlan, setBillingPlan] = React.useState('' as BillingPlan);
  const [slideCount, setSlideCount] = React.useState(1);
  const [locations, setLocations] = React.useState([] as DigiknowLocation[]);
  const [firstDate, setFirstDate] = React.useState('');
  const [lastDate, setLastDate] = React.useState('');
  const [adFileKey, setAdFileKey] = React.useState('');
  const [adFile, setAdFile] = React.useState(undefined as File | undefined);

  /**
   * Save ad details to state and move to next step.
   */
  function setAdFormDetails(adDetails: AdRequestFormFields): void {
    setAdName(adDetails.adName);
    R.unless(R.isNil, setComments, adDetails.comments as string);
    setOrganization(adDetails.organization);
    setOrgType(adDetails.organizationType);
    setPhoneNum(adDetails.contactPhoneNum);
    R.unless(R.isNil, setRechargeNum, adDetails.rechargeNumber as string);
    setBillingPlan(adDetails.billingPlan as BillingPlan);
    setSlideCount(adDetails.slideCount);
    setLocations(adDetails.locations);
    setFirstDate(adDetails.firstDate);
    setLastDate(adDetails.lastDate);
    setStep(step + 1);
  }

  function buildAdRequest(): AdRequestProffer {
    const [firstName, lastName] = splitName(user);
    // Form validation ensures valid rechargeNum on these orgTypes.
    // On all other types rechargeNum is not applicable.
    const recharge = R.includes(orgType, ['Campus Department', 'Campus Department w/ Sponsorship Logo'])
      ? rechargeNum
      : undefined;
    return {
      adName,
      userFirstName: firstName,
      userLastName: lastName,
      contactPhoneNum: phoneNum,
      comments,
      organization,
      organizationType: orgType as DigiknowOrgType,
      billingPlan,
      rechargeNumber: recharge,
      slideCount,
      locations,
      firstDate,
      lastDate,
      adFileKey
    };
  }

  function saveAdRequest(ad: AdRequestProffer): Promise<Response> {
    const userKey = btoa(userEmail(user));
    const url = `/api/v1/requests/${userKey}/${adKey}`;
    return fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': apiToken || 'unknown-token',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(ad)
    });
  }

  const stepContent = (step: number): React.ReactElement => {
    switch (step) {
      case 0:
        return <AdDetailsForm setAdDetails={setAdFormDetails} proffer={buildAdRequest()} />;

      case 1:
        return <AdUploadForm
          adFile={adFile}
          setAdFile={setAdFile}
          setAdFileKey={setAdFileKey}
          nextStep={(): void => setStep(step + 1)}
          prevStep={(): void => setStep(step - 1)} />;

      case 2:
        return <Review adRequest={buildAdRequest()} />;

      default:
        throw new Error('Unknown step');
    }
  };

  const handleNext = (): void => {
    if (step === 2) {
      saveAdRequest(buildAdRequest())
        .then(async result => {
          console.debug('Save ad details result: %o', await result.json());
          switch (result.ok) {
            case true:
              console.info('Saved ad details');
              setStep(step + 1);
              break;

            case false:
              if (result.status === 403 || result.status === 401) {
                alert('Your session has expired, due to inactivity. Please reload the page and try again.');
                break;
              }
              console.error('Unable to save ad details');
              // TODO: Better looking error alert
              alert('There was a problem saving your ad. Try again or contact support.');
              break;
          }
        })
        .catch(err => {
          console.error('Error saving ad details: %o', err);
        });
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = (): void => {
    setStep(step - 1);
  };

  const classes = useStyles();

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justify="center"
    >
      <Paper className={classes.paper}>
        <Typography component="h2" align="center">
          Submit new DigiKnow ad</Typography>

        <Stepper activeStep={step} className={classes.stepper}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <>
          {step === steps.length
            ? <Redirect to={{pathname: '/received'}} />
            : (
              <>
                {stepContent(step)}
                <div className={classes.buttons}>
                  {step !== 0 && step !== 1 && (
                    <Button onClick={handleBack} className={classes.button}>
                      Back
                    </Button>
                  )}
                  {step !== 0 && step !== 1 && (
                    <Button variant="contained"
                      color="primary"
                      onClick={handleNext}
                      className={classes.button}>
                      {step === steps.length - 1 ? 'Submit ad' : 'Next'}
                    </Button>
                  )}
                </div>
              </>
            )}
        </>
      </Paper>
    </Grid>
  );
};
export default CreateAd;
